import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  TextField,
  Container,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Link,
  FormGroup,
  Checkbox
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

// Function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const POCEvaluationForm = () => {
  const { tr } = useLanguage();
  const navigate = useNavigate();
  const query = useQuery();
  const conversationId = query.get('conversationId');

  console.log('Debug - Conversation ID from URL:', conversationId); // Debug log

  const [formData, setFormData] = useState({
    teachingAccuracy: '',
    nodeEvaluation: '',
    errorCorrectionEvaluation: '',
    checkpointEvaluation: '',
    technicalSuggestions: '',
    teachingIssues: {
      scriptMismatch: false,
      impreciseContent: false,
      inappropriateContext: false,
      ambiguousContent: false,
      other: false
    },
    nodeIssues: {
      falsePositive: false,
      falseNegative: false,
      inconsistentJudgment: false,
      delayedJudgment: false,
      other: false
    },
    errorCorrectionIssues: {
      missedErrors: false,
      noSuggestions: false,
      noContext: false,
      unclearSuggestions: false,
      impracticalSuggestions: false,
      other: false
    },
    checkpointIssues: {
      falsePass: false,
      falseFail: false,
      unreasonableDelay: false,
      unclearDelay: false,
      other: false
    }
  });

  // Add state for other issue descriptions
  const [otherIssueDetails, setOtherIssueDetails] = useState({
    teachingOtherDetails: '',
    nodeOtherDetails: '',
    errorCorrectionOtherDetails: '',
    checkpointOtherDetails: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [memberId, setMemberId] = useState(null);

  // Get member_id from localStorage when component mounts
  useEffect(() => {
    const getMemberId = () => {
      const userData = localStorage.getItem('user');
      console.log('Debug - Raw user data from localStorage:', userData); // Debug log
      if (userData) {
        try {
          const user = JSON.parse(userData);
          console.log('Debug - Parsed user object:', user); // Debug log
          if (user && user.id) {
            console.log('Debug - User ID:', user.id); // Debug log
            setMemberId(user.id);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      } else {
        console.log('Debug - No user data found in localStorage'); // Debug log
      }
    };

    getMemberId();
  }, []);
  useEffect(() => {
    axios.get(`${API_URL}/api/storage/health`)
      .then(() => setConnectionStatus('Connected to VM database'))
      .catch((error) => setConnectionStatus(`Database connection failed: ${error.message}`));
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      // Handle checkbox changes
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: checked
        }
      }));
    } else if (name.includes('OtherDetails')) {
      // Handle other details textarea changes
      setOtherIssueDetails(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      // Handle radio button changes
      const newFormData = {
        ...formData,
        [name]: value
      };

      // Reset checkbox values if changing radio selections to values that don't show checkboxes
      if (name === 'teachingAccuracy' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.teachingIssues = {
          scriptMismatch: false,
          impreciseContent: false,
          inappropriateContext: false,
          ambiguousContent: false,
          other: false
        };
      } else if (name === 'nodeEvaluation' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.nodeIssues = {
          falsePositive: false,
          falseNegative: false,
          inconsistentJudgment: false,
          delayedJudgment: false,
          other: false
        };
      } else if (name === 'errorCorrectionEvaluation' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.errorCorrectionIssues = {
          missedErrors: false,
          noSuggestions: false,
          noContext: false,
          unclearSuggestions: false,
          impracticalSuggestions: false,
          other: false
        };
      } else if (name === 'checkpointEvaluation' && value !== 'acceptable' && value !== 'needsImprovement' && value !== 'insufficient') {
        newFormData.checkpointIssues = {
          falsePass: false,
          falseFail: false,
          unreasonableDelay: false,
          unclearDelay: false,
          other: false
        };
      }

      setFormData(newFormData);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate form data
    const requiredFields = ['teachingAccuracy', 'nodeEvaluation', 'errorCorrectionEvaluation', 'checkpointEvaluation'];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      setSnackbar({
        open: true,
        message: tr('請填寫所有評估項目', 'Please complete all evaluation items'),
        severity: 'error'
      });
      return;
    }

    setIsLoading(true);

    try {
      // Get member_id from localStorage
      const userData = localStorage.getItem('user');
      console.log('Debug - Raw user data before submission:', userData); // Debug log
      let currentMemberId = null;

      if (userData) {
        try {
          const user = JSON.parse(userData);
          currentMemberId = user.id;
          console.log('Debug - Current member ID before submission:', currentMemberId); // Debug log
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }

      // Prepare issue data with other details
      const teachingIssuesWithDetails = {
        ...formData.teachingIssues,
        otherDetails: formData.teachingIssues.other ? otherIssueDetails.teachingOtherDetails : ''
      };

      const nodeIssuesWithDetails = {
        ...formData.nodeIssues,
        otherDetails: formData.nodeIssues.other ? otherIssueDetails.nodeOtherDetails : ''
      };

      const errorCorrectionIssuesWithDetails = {
        ...formData.errorCorrectionIssues,
        otherDetails: formData.errorCorrectionIssues.other ? otherIssueDetails.errorCorrectionOtherDetails : ''
      };

      const checkpointIssuesWithDetails = {
        ...formData.checkpointIssues,
        otherDetails: formData.checkpointIssues.other ? otherIssueDetails.checkpointOtherDetails : ''
      };

      await axios.post(`${API_URL}/api/storage/poc-evaluations`, {
        member_id: currentMemberId ? parseInt(currentMemberId) : null,
        conversation_id: conversationId ? parseInt(conversationId) : null,
        teaching_accuracy: formData.teachingAccuracy,
        node_evaluation: formData.nodeEvaluation,
        error_correction_evaluation: formData.errorCorrectionEvaluation,
        checkpoint_evaluation: formData.checkpointEvaluation,
        technical_suggestions: formData.technicalSuggestions || '',
        teaching_issues: teachingIssuesWithDetails,
        node_issues: nodeIssuesWithDetails,
        error_correction_issues: errorCorrectionIssuesWithDetails,
        checkpoint_issues: checkpointIssuesWithDetails
      });

      // Reset form after successful submission
      setFormData({
        teachingAccuracy: '',
        nodeEvaluation: '',
        errorCorrectionEvaluation: '',
        checkpointEvaluation: '',
        technicalSuggestions: '',
        teachingIssues: {
          scriptMismatch: false,
          impreciseContent: false,
          inappropriateContext: false,
          ambiguousContent: false,
          other: false
        },
        nodeIssues: {
          falsePositive: false,
          falseNegative: false,
          inconsistentJudgment: false,
          delayedJudgment: false,
          other: false
        },
        errorCorrectionIssues: {
          missedErrors: false,
          noSuggestions: false,
          noContext: false,
          unclearSuggestions: false,
          impracticalSuggestions: false,
          other: false
        },
        checkpointIssues: {
          falsePass: false,
          falseFail: false,
          unreasonableDelay: false,
          unclearDelay: false,
          other: false
        }
      });

      setOtherIssueDetails({
        teachingOtherDetails: '',
        nodeOtherDetails: '',
        errorCorrectionOtherDetails: '',
        checkpointOtherDetails: ''
      });

      setSnackbar({
        open: true,
        message: tr('表單已成功提交', 'Form submitted successfully'),
        severity: 'success'
      });

      // Navigate to results page after successful submission
      setTimeout(() => {
        navigate('/poc-evaluation-results');
      }, 2000);

    } catch (err) {
      console.error('Error submitting form:', err);
      setSnackbar({
        open: true,
        message: `${tr('提交失敗', 'Submission failed')}: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight="bold">
          {tr('POC 系統驗測表單', 'POC System Evaluation Form')}
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" sx={{ mb: 2 }}>
          {tr('請根據您的測試經驗，提供量化的評估結果', 'Provide a quantitative evaluation based on your testing experience.')}
        </Typography>

        {conversationId && (
          <Typography variant="subtitle2" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
            {tr('評估對話 ID', 'Conversation ID')}: {conversationId}
          </Typography>
        )}

        {connectionStatus && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Typography
              color={connectionStatus.includes('failed') ? 'error' : 'success.main'}
              variant="body2"
            >
              {connectionStatus.includes('Connected') ? tr('已連線至 VM 資料庫', connectionStatus) : connectionStatus}
            </Typography>
          </Box>
        )}

        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
            {tr('技術需求達成度評估', 'Technical Requirements Evaluation')}
          </Typography>
          <Typography variant="body1" paragraph>
            {tr('本區塊評估系統的技術功能是否符合預期目標，請依據您的測試經驗，選擇最符合的準確率區間', 'Evaluate whether the system’s technical capabilities meet their goals, then select the accuracy range that best matches your experience.')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {/* Teaching Guidance Accuracy */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                {tr('教學引導準確性:', 'Coaching Guidance Accuracy:')}
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {tr('評估陪練模式下每個情境單元的教學引導是否與富邦提供的範例腳本相符', 'Evaluate whether the guidance for each practice scenario matches the sample scripts provided by Fubon.')}
              </Typography>
              <RadioGroup
                name="teachingAccuracy"
                value={formData.teachingAccuracy}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label={tr('優良 (>95%)', 'Excellent (>95%)')} />
                <FormControlLabel value="good" control={<Radio />} label={tr('良好 (85-95%)', 'Good (85-95%)')} />
                <FormControlLabel value="acceptable" control={<Radio />} label={tr('可接受 (70-85%)', 'Acceptable (70-85%)')} />
                <FormControlLabel value="needsImprovement" control={<Radio />} label={tr('需改進 (50-70%)', 'Needs improvement (50-70%)')} />
                <FormControlLabel value="insufficient" control={<Radio />} label={tr('不足 (<50%)', 'Insufficient (<50%)')} />
              </RadioGroup>

              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  mb: 1,
                  display: formData.teachingAccuracy === 'acceptable' || formData.teachingAccuracy === 'needsImprovement' || formData.teachingAccuracy === 'insufficient' ? 'block' : 'none'
                }}
              >
                {tr('發現的問題（可複選）', 'Issues found (select all that apply)')}
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.teachingAccuracy === 'acceptable' || formData.teachingAccuracy === 'needsImprovement' || formData.teachingAccuracy === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.teachingIssues.scriptMismatch}
                    onChange={handleChange}
                    name="teachingIssues.scriptMismatch"
                  />}
                  label={tr('建議內容與腳本不符', 'Guidance does not match the script')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.teachingIssues.impreciseContent}
                    onChange={handleChange}
                    name="teachingIssues.impreciseContent"
                  />}
                  label={tr('建議內容不夠精確', 'Guidance is not precise enough')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.teachingIssues.inappropriateContext}
                    onChange={handleChange}
                    name="teachingIssues.inappropriateContext"
                  />}
                  label={tr('建議不適用當前情境', 'Guidance does not fit the scenario')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.teachingIssues.ambiguousContent}
                    onChange={handleChange}
                    name="teachingIssues.ambiguousContent"
                  />}
                  label={tr('建議內容語意模糊', 'Guidance is ambiguous')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.teachingIssues.other}
                    onChange={handleChange}
                    name="teachingIssues.other"
                  />}
                  label={tr('其他', 'Other')}
                />
                {formData.teachingIssues.other && (
                  <TextField
                    fullWidth
                    name="teachingOtherDetails"
                    label={tr('請說明其他問題', 'Describe the other issue')}
                    multiline
                    rows={2}
                    value={otherIssueDetails.teachingOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Node Evaluation Accuracy */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                {tr('節點判斷正確性:', 'Node Judgment Accuracy:')}
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {tr('評估系統是否能正確判斷訓練情境中各個節點的完成狀態', 'Evaluate whether the system correctly determines the completion status of each scenario node.')}
              </Typography>
              <RadioGroup
                name="nodeEvaluation"
                value={formData.nodeEvaluation}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label={tr('優良 (>95%)', 'Excellent (>95%)')} />
                <FormControlLabel value="good" control={<Radio />} label={tr('良好 (85-95%)', 'Good (85-95%)')} />
                <FormControlLabel value="acceptable" control={<Radio />} label={tr('可接受 (70-85%)', 'Acceptable (70-85%)')} />
                <FormControlLabel value="needsImprovement" control={<Radio />} label={tr('需改進 (50-70%)', 'Needs improvement (50-70%)')} />
                <FormControlLabel value="insufficient" control={<Radio />} label={tr('不足 (<50%)', 'Insufficient (<50%)')} />
              </RadioGroup>

              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  mb: 1,
                  display: formData.nodeEvaluation === 'acceptable' || formData.nodeEvaluation === 'needsImprovement' || formData.nodeEvaluation === 'insufficient' ? 'block' : 'none'
                }}
              >
                {tr('發現的問題（可複選）', 'Issues found (select all that apply)')}
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.nodeEvaluation === 'acceptable' || formData.nodeEvaluation === 'needsImprovement' || formData.nodeEvaluation === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.nodeIssues.falsePositive}
                    onChange={handleChange}
                    name="nodeIssues.falsePositive"
                  />}
                  label={tr('誤判判斷為達標', 'Incorrectly marked complete')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.nodeIssues.falseNegative}
                    onChange={handleChange}
                    name="nodeIssues.falseNegative"
                  />}
                  label={tr('誤判判斷為未達標', 'Incorrectly marked incomplete')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.nodeIssues.inconsistentJudgment}
                    onChange={handleChange}
                    name="nodeIssues.inconsistentJudgment"
                  />}
                  label={tr('誤判判斷不一致', 'Inconsistent judgment')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.nodeIssues.delayedJudgment}
                    onChange={handleChange}
                    name="nodeIssues.delayedJudgment"
                  />}
                  label={tr('誤判判斷延遲', 'Delayed judgment')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.nodeIssues.other}
                    onChange={handleChange}
                    name="nodeIssues.other"
                  />}
                  label={tr('其他', 'Other')}
                />
                {formData.nodeIssues.other && (
                  <TextField
                    fullWidth
                    name="nodeOtherDetails"
                    label={tr('請說明其他問題', 'Describe the other issue')}
                    multiline
                    rows={2}
                    value={otherIssueDetails.nodeOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Error Correction Evaluation */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                {tr('評估報告的糾錯狀況:', 'Report Error Detection and Correction:')}
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {tr('評估系統對常見錯誤和違反全域規則的識別能力，以及提供的糾錯建議品質', 'Evaluate how well the system identifies common mistakes and global-rule violations, and the quality of its corrections.')}
              </Typography>
              <RadioGroup
                name="errorCorrectionEvaluation"
                value={formData.errorCorrectionEvaluation}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label={tr('優良 (>95%)', 'Excellent (>95%)')} />
                <FormControlLabel value="good" control={<Radio />} label={tr('良好 (85-95%)', 'Good (85-95%)')} />
                <FormControlLabel value="acceptable" control={<Radio />} label={tr('可接受 (70-85%)', 'Acceptable (70-85%)')} />
                <FormControlLabel value="needsImprovement" control={<Radio />} label={tr('需改進 (50-70%)', 'Needs improvement (50-70%)')} />
                <FormControlLabel value="insufficient" control={<Radio />} label={tr('不足 (<50%)', 'Insufficient (<50%)')} />
              </RadioGroup>

              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  mb: 1,
                  display: formData.errorCorrectionEvaluation === 'acceptable' || formData.errorCorrectionEvaluation === 'needsImprovement' || formData.errorCorrectionEvaluation === 'insufficient' ? 'block' : 'none'
                }}
              >
                {tr('發現的問題（可複選）', 'Issues found (select all that apply)')}
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.errorCorrectionEvaluation === 'acceptable' || formData.errorCorrectionEvaluation === 'needsImprovement' || formData.errorCorrectionEvaluation === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.errorCorrectionIssues.missedErrors}
                    onChange={handleChange}
                    name="errorCorrectionIssues.missedErrors"
                  />}
                  label={tr('未能識別特定類型錯誤', 'Failed to identify a specific error type')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.errorCorrectionIssues.noSuggestions}
                    onChange={handleChange}
                    name="errorCorrectionIssues.noSuggestions"
                  />}
                  label={tr('未能提供修改建議', 'No correction suggested')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.errorCorrectionIssues.noContext}
                    onChange={handleChange}
                    name="errorCorrectionIssues.noContext"
                  />}
                  label={tr('未能提供使用情境', 'No usage context provided')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.errorCorrectionIssues.unclearSuggestions}
                    onChange={handleChange}
                    name="errorCorrectionIssues.unclearSuggestions"
                  />}
                  label={tr('建議過於不明確', 'Suggestion is unclear')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.errorCorrectionIssues.impracticalSuggestions}
                    onChange={handleChange}
                    name="errorCorrectionIssues.impracticalSuggestions"
                  />}
                  label={tr('系統建議不實用', 'Suggestion is impractical')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.errorCorrectionIssues.other}
                    onChange={handleChange}
                    name="errorCorrectionIssues.other"
                  />}
                  label={tr('其他', 'Other')}
                />
                {formData.errorCorrectionIssues.other && (
                  <TextField
                    fullWidth
                    name="errorCorrectionOtherDetails"
                    label={tr('請說明其他問題', 'Describe the other issue')}
                    multiline
                    rows={2}
                    value={otherIssueDetails.errorCorrectionOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Checkpoint Evaluation */}
            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                {tr('查核點判斷準確性:', 'Checkpoint Judgment Accuracy:')}
              </FormLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {tr('評估系統是否能正確判斷各節點內查核點的達成情況', 'Evaluate whether the system correctly determines whether checkpoints are met within each node.')}
              </Typography>
              <RadioGroup
                name="checkpointEvaluation"
                value={formData.checkpointEvaluation}
                onChange={handleChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel value="excellent" control={<Radio />} label={tr('優良 (>95%)', 'Excellent (>95%)')} />
                <FormControlLabel value="good" control={<Radio />} label={tr('良好 (85-95%)', 'Good (85-95%)')} />
                <FormControlLabel value="acceptable" control={<Radio />} label={tr('可接受 (70-85%)', 'Acceptable (70-85%)')} />
                <FormControlLabel value="needsImprovement" control={<Radio />} label={tr('需改進 (50-70%)', 'Needs improvement (50-70%)')} />
                <FormControlLabel value="insufficient" control={<Radio />} label={tr('不足 (<50%)', 'Insufficient (<50%)')} />
              </RadioGroup>

              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  mb: 1,
                  display: formData.checkpointEvaluation === 'acceptable' || formData.checkpointEvaluation === 'needsImprovement' || formData.checkpointEvaluation === 'insufficient' ? 'block' : 'none'
                }}
              >
                {tr('發現的問題（可複選）', 'Issues found (select all that apply)')}
              </Typography>
              <FormGroup sx={{ ml: 2, display: formData.checkpointEvaluation === 'acceptable' || formData.checkpointEvaluation === 'needsImprovement' || formData.checkpointEvaluation === 'insufficient' ? 'block' : 'none' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.checkpointIssues.falsePass}
                    onChange={handleChange}
                    name="checkpointIssues.falsePass"
                  />}
                  label={tr('查核點判斷為達標', 'Incorrectly marked checkpoint complete')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.checkpointIssues.falseFail}
                    onChange={handleChange}
                    name="checkpointIssues.falseFail"
                  />}
                  label={tr('查核點判斷為未達標', 'Incorrectly marked checkpoint incomplete')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.checkpointIssues.unreasonableDelay}
                    onChange={handleChange}
                    name="checkpointIssues.unreasonableDelay"
                  />}
                  label={tr('查核點延遲不合理', 'Unreasonable checkpoint delay')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.checkpointIssues.unclearDelay}
                    onChange={handleChange}
                    name="checkpointIssues.unclearDelay"
                  />}
                  label={tr('查核點延遲不明確', 'Unclear checkpoint delay')}
                />
                <FormControlLabel
                  control={<Checkbox
                    checked={formData.checkpointIssues.other}
                    onChange={handleChange}
                    name="checkpointIssues.other"
                  />}
                  label={tr('其他', 'Other')}
                />
                {formData.checkpointIssues.other && (
                  <TextField
                    fullWidth
                    name="checkpointOtherDetails"
                    label={tr('請說明其他問題', 'Describe the other issue')}
                    multiline
                    rows={2}
                    value={otherIssueDetails.checkpointOtherDetails}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              </FormGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* Suggestions */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                {tr('補充建議', 'Additional Suggestions')}
              </Typography>
              <Typography variant="body2" paragraph>
                {tr('如有其他未在上方評估項目中涵蓋的技術功能改進建議，請在此補充', 'Add any technical improvement suggestions not covered by the items above.')}
              </Typography>

              <TextField
                fullWidth
                name="technicalSuggestions"
                label={tr('技術功能改進建議', 'Technical improvement suggestions')}
                multiline
                rows={4}
                value={formData.technicalSuggestions}
                onChange={handleChange}
                placeholder={tr('請提供具體的技術功能改進建議...', 'Provide specific technical improvement suggestions...')}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontSize: '1rem'
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  tr('提交回饋', 'Submit Feedback')
                )}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                component={RouterLink}
                to="/poc-evaluation-results"
                startIcon={<AssessmentIcon />}
                color="secondary"
                sx={{ textDecoration: 'none', mt: 2 }}
              >
                {tr('查看已提交的評估結果', 'View Submitted Evaluations')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default POCEvaluationForm;
